import { lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';

import { AuthGuard, GuestGuard, PasswordChangeGuard } from '@/app/guards';
import { AppLayout } from '@/layouts/AppLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { moduleRoutes } from '@/routes/module-routes';
import { ROUTES } from '@/routes/paths';

/**
 * The route table.
 *
 * Three layers, and the order matters: the guest branch (sign-in, password reset)
 * renders under `AuthLayout`, the authenticated branch under `AppLayout`, and the
 * module routes are generated rather than listed — see `module-routes.tsx`.
 *
 * Pages are code-split so the initial bundle carries the shell and the dashboard
 * only; the other thirty-odd modules arrive when they are first opened.
 */

const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));

const ProfilePage = lazy(() => import('@/pages/account/ProfilePage'));
const ChangePasswordPage = lazy(() => import('@/pages/account/ChangePasswordPage'));

const FieldLayout = lazy(() => import('@/pages/field/FieldLayout'));
const AssignedSurveysPage = lazy(() => import('@/pages/field/AssignedSurveysPage'));
const SurveyWizardPage = lazy(() => import('@/pages/field/SurveyWizardPage'));

const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const ForbiddenPage = lazy(() => import('@/pages/ForbiddenPage'));

export const router = createBrowserRouter([
  {
    element: <GuestGuard />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: ROUTES.login, element: <LoginPage /> },
          { path: ROUTES.forgotPassword, element: <ForgotPasswordPage /> },
          { path: ROUTES.resetPassword, element: <ResetPasswordPage /> },
        ],
      },
    ],
  },
  {
    element: <AuthGuard />,
    children: [
      {
        element: <PasswordChangeGuard />,
        children: [
          /**
           * The field survey app.
           *
           * A sibling of `AppLayout`, not a route inside it. It is the same
           * application — same session, same API client, same permissions — but
           * a different shell: no sidebar of forty modules, no global search, no
           * breadcrumbs. Every one of those is a way to wander off a survey
           * somebody is standing on a roof to finish, and none of them fits on a
           * 390px screen worth the room it costs.
           *
           * Registered before the `AppLayout` branch so `/field` resolves here
           * rather than being swallowed by that branch's catch-all.
           *
           * No `ModuleGuard`: the routes underneath read and write the caller's
           * own surveys through endpoints that already check
           * `site_surveys:view` and `:update`, and a second permission gate in
           * front of them would only be a way to lock a surveyor out of work
           * that has been assigned to them.
           */
          {
            path: 'field',
            element: <FieldLayout />,
            children: [
              { index: true, element: <AssignedSurveysPage /> },
              { path: 'surveys/:id', element: <SurveyWizardPage /> },
              { path: '*', element: <Navigate to="/field" replace /> },
            ],
          },
          {
            element: <AppLayout />,
            children: [
              { index: true, element: <Navigate to={ROUTES.dashboard} replace /> },

              /**
               * Your own account, not the Settings module. These carry no
               * permission because everyone has one — and they are listed before
               * the generated module routes so `/settings/profile` resolves here
               * rather than being swallowed by the `company_settings` splat,
               * which would demand an admin permission to read your own name.
               */
              { path: ROUTES.profile, element: <ProfilePage /> },
              { path: ROUTES.changePassword, element: <ChangePasswordPage /> },

              /**
               * Where the accounting sync used to live.
               *
               * It moved into the Integrations hub when the framework grew from
               * one provider to nine. Redirected rather than dropped because
               * this path is in people's bookmarks and in the deploy notes for
               * the finance build, and a 404 on a screen somebody used
               * yesterday reads as a broken release.
               */
              {
                path: '/finance/tally-sync/*',
                element: <Navigate to="/integrations/queue" replace />,
              },

              ...moduleRoutes,

              { path: ROUTES.forbidden, element: <ForbiddenPage /> },
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
]);
